import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { catalogueTitle } from '../catalogue-titles';
import {
    Component,
    OnInit,
    ViewEncapsulation,
    inject,
    ChangeDetectorRef,
    PLATFORM_ID,
    signal,
    computed,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { MaterialModule } from '../../material.module';

import { UsersService }       from '@app/shared/services/entities/users.service';
import { RolesService }       from '@app/shared/services/entities/roles.service';
import { ModulesService }     from '@app/shared/services/modules.service';
import { PermissionsService } from '@app/shared/services/permissions.service';
import { Role }               from '@app/shared/models/entities/roles.model';
import { ModuleModel }        from '@app/shared/models/entities/module.model';
import { PermissionModel }    from '@app/shared/models/entities/permission.model';
import { sortAlphabetically } from '@app/shared/utils/sort.utils';

// Servicios de entidad — necesarios para calcular cuántos registros
// existen por catálogo (badge superior y por tarjeta).
import { AffilatesService }              from '@app/shared/services/entities/affilates.service';
import { BrandsService }                 from '@app/shared/services/entities/brands.service';
import { ClientsService }                from '@app/shared/services/entities/clients.service';
import { EquipmentsService }             from '@app/shared/services/entities/equipments.service';
import { HospitalServicesService }       from '@app/shared/services/entities/hospitalServices.service';
import { KitService }                    from '@app/shared/services/entities/kit.service';
import { DoctorsService }                from '@app/shared/services/entities/doctors.service';
import { SubstitutesService }            from '@app/shared/services/entities/substitutes.service';
import { InstrumentalsService }          from '@app/shared/services/entities/instrumentals.service';
import { MedicalGasTanksService }        from '@app/shared/services/entities/medicalGasTanks.service';
import { ManufacturerService }           from '@app/shared/services/entities/manufacturers.service';
import { MeasurementInstrumentsService } from '@app/shared/services/entities/measurementInstruments.service';
import { MeasurementUnitsService }       from '@app/shared/services/entities/measurementUnits.service';
import { PaymentMethodsService }         from '@app/shared/services/entities/paymentMethods.service';
import { PlacesService }                 from '@app/shared/services/entities/places.service';
import { ProcedureService }              from '@app/shared/services/entities/procedure.service';
import { ProductsService }               from '@app/shared/services/entities/products.service';
import { SparePartsService }             from '@app/shared/services/entities/spareParts.service';
import { SpecialtyService }              from '@app/shared/services/entities/specialty.service';
import { SupplierService }               from '@app/shared/services/entities/supplier.service';
import { WarehousesService }             from '@app/shared/services/entities/warehouse.service';
import { CoursesService }                from '@app/shared/services/entities/courses.service';
import { TaxRegimesService }             from '@app/shared/services/entities/taxRegimes.service';
import { AccessoriesService }            from '@app/shared/services/entities/accessories.service';
import { PeripheralEquipmentService }    from '@app/shared/services/entities/peripheralEquipment.service';
import { FixedAssetConfigurationsService }        from '@app/shared/services/entities/fixedAssetConfigurations.service';
import { FixedAssetServiceConfigurationsService } from '@app/shared/services/entities/fixedAssetServiceConfigurations.service';
import { SuppliesService }               from '@app/shared/services/entities/supplies.service';
import { InstitutionsService }           from '@app/shared/services/entities/institutions.service';

/**
 * Tarjetón de un catálogo (item visible en el menú).
 * Construido dinámicamente desde un `ModuleModel` de BD.
 */
export interface CatalogueCard {
    title:       string;
    description: string;
    icon:        string;
    route:       string;   // ej. '/catalogues/warehouses'
}

const PERMISSION_VER = 'LEER';

/** Mínimo contrato de servicio que necesitamos para contar registros. */
interface CountableService {
    search(data: any): Promise<any>;
}

@Component({
    selector:      'app-catalogues-menu',
    standalone:    true,
    templateUrl:   './catalogues-menu.component.html',
    styleUrls:     ['./catalogues-menu.component.scss'],
    imports:       [PageLoaderComponent, PageTitleComponent, MaterialModule],
    encapsulation: ViewEncapsulation.Emulated,
})
export class CataloguesMenuComponent implements OnInit {

    private router             = inject(Router);
    private cdr                = inject(ChangeDetectorRef);
    private platformId         = inject(PLATFORM_ID);
    private usersService       = inject(UsersService);
    private rolesService       = inject(RolesService);
    private modulesService     = inject(ModulesService);
    private permissionsService = inject(PermissionsService);

    // Servicios de los catálogos para contar registros.
    private affiliateService             = inject(AffilatesService);
    private brandService                 = inject(BrandsService);
    private clientService                = inject(ClientsService);
    private equipmentsService            = inject(EquipmentsService);
    private hospitalServicesService      = inject(HospitalServicesService);
    private kitService                   = inject(KitService);
    private doctorsService               = inject(DoctorsService);
    private substitutesService           = inject(SubstitutesService);
    private instrumentalService          = inject(InstrumentalsService);
    private medicalGasTanksService       = inject(MedicalGasTanksService);
    private manufacturerService          = inject(ManufacturerService);
    private measurementInstrumentsService = inject(MeasurementInstrumentsService);
    private measurementUnitsService      = inject(MeasurementUnitsService);
    private paymentMethodsService        = inject(PaymentMethodsService);
    private placesService                = inject(PlacesService);
    private procedureService             = inject(ProcedureService);
    private productsService              = inject(ProductsService);
    private sparePartsService            = inject(SparePartsService);
    private specialtyService             = inject(SpecialtyService);
    private supplierService              = inject(SupplierService);
    private warehousesService            = inject(WarehousesService);
    private coursesService               = inject(CoursesService);
    private taxRegimesService            = inject(TaxRegimesService);
    private accessoriesService           = inject(AccessoriesService);
    private peripheralEquipmentService   = inject(PeripheralEquipmentService);
    private fixedAssetConfigurationsService        = inject(FixedAssetConfigurationsService);
    private fixedAssetServiceConfigurationsService = inject(FixedAssetServiceConfigurationsService);
    private suppliesService              = inject(SuppliesService);
    private institutionsService          = inject(InstitutionsService);

    /** Título del módulo = etiqueta visible del catálogo de Módulos. */
    title = this.modulesService.moduleTitle('/catalogues', 'Catálogos');

    /**
     * Tarjetas a mostrar, en orden alfabético por título.
     * Vacío hasta que termine la carga inicial.
     */
    menuCards = signal<CatalogueCard[]>([]);

    /**
     * Conteo de registros activos por ruta (`/catalogues/<segmento>`).
     * - `undefined`  → todavía no se obtuvo / no hay servicio mapeado
     * - `number`     → cantidad de registros (length del data del search)
     *
     * Lo expone como signal para que el template reaccione automáticamente
     * conforme se vayan resolviendo los .search() en paralelo.
     */
    routeCounts = signal<Record<string, number | undefined>>({});

    /**
     * Suma total de todos los conteos disponibles. Lo usamos para el
     * chip de "Total" en la cabecera del menú.
     */
    totalCount = computed(() =>
        Object.values(this.routeCounts())
            .filter((n): n is number => typeof n === 'number')
            .reduce((acc, n) => acc + n, 0),
    );

    /**
     * Estado de la carga. El template muestra spinner mientras 'loading' y
     * un mensaje específico para 'no-role' / 'empty' / 'error'.
     */
    state = signal<'loading' | 'ready' | 'empty' | 'no-role' | 'error'>('loading');

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
        if (!isPlatformBrowser(this.platformId)) return;

        await this.loadVisibleCatalogues();
        this.cdr.detectChanges();

        // Ya con las tarjetas visibles, lanzamos los conteos en paralelo.
        // No bloqueamos el render: el badge aparece cuando llega cada uno.
        this.loadRouteCounts();
    }

    /**
     * Carga las tarjetas que el usuario tiene permitido ver.
     *
     * Pipeline:
     *   1. Usuario actual → su `role` ID
     *   2. Rol → `navAccessTree` y `accessPermissions`
     *   3. Todos los módulos con route `/catalogues/<algo>` (sub-módulos)
     *   4. Filtrar:
     *        a) deben estar en `role.navAccessTree`
     *        b) deben tener el permiso VER en `role.accessPermissions`
     *   5. Convertir a `CatalogueCard[]`, ordenar y publicar.
     *
     * Si algo falla, deja el estado en 'error' y un array vacío.
     */
    private async loadVisibleCatalogues(): Promise<void> {
        try {
            // ─── 1. Usuario ──────────────────────────────────────────
            let user = this.usersService.currentUser();
            if (!user) {
                await this.usersService.loadCurrentUser();
                user = this.usersService.currentUser();
            }
            if (!user) {
                this.state.set('no-role');
                return;
            }
            if (!user.role) {
                this.state.set('no-role');
                return;
            }

            // ─── 2. Rol ──────────────────────────────────────────────
            const rolesResp = await this.rolesService.search({
                searchType: 'default',
                condition: { id: user.role },
            });
            const role: Role | undefined = rolesResp.data?.[0];
            if (!role) {
                this.state.set('no-role');
                return;
            }

            // ─── 3. Módulos del sistema, filtrar sub-rutas /catalogues/X ─
            const modulesResp = await this.modulesService.search({
                searchType: 'default',
                condition: { deleted: false, active: true },
            });
            const allModules: ModuleModel[] = modulesResp.data ?? [];

            const catalogueModules = allModules.filter(m =>
                this.isCatalogueSubRoute(m.route)
            );

            // ─── 4. Permisos del sistema (mapeo ID → name) ───────────
            const permsResp = await this.permissionsService.search({
                searchType: 'default',
                condition: { deleted: false },
            });
            const allPerms: PermissionModel[] = permsResp.data ?? [];
            const permIdToName = new Map<string, string>(
                allPerms.map(p => [p.id!, (p.name ?? '').toUpperCase()])
            );

            // ─── 5. Filtrar por navAccessTree + VER, mapear a tarjetas ───
            const cards: CatalogueCard[] = catalogueModules
                .filter(m => role.navAccessTree?.includes(m.id!))
                .filter(m => this.userHasViewPermission(role, m.id!, permIdToName))
                .map(m => this.moduleToCard(m));

            const sorted = sortAlphabetically(cards, 'title');
            this.menuCards.set(sorted);
            this.state.set(sorted.length > 0 ? 'ready' : 'empty');

        } catch (err) {
            console.error('[CataloguesMenu] Error cargando catálogos:', err);
            this.state.set('error');
            this.menuCards.set([]);
        }
    }

    /**
     * True si la ruta es del tipo '/catalogues/<segmento>' (exactamente 2
     * segmentos, el primero 'catalogues').
     *
     * Excluye '/catalogues/menu' (el propio menú no debe ser su propia tarjeta).
     */
    private isCatalogueSubRoute(route: string | undefined): boolean {
        if (!route) return false;
        const segments = route
            .split('?')[0]
            .split('#')[0]
            .replace(/^\/+|\/+$/g, '')
            .split('/')
            .filter(s => s.length > 0);

        return segments.length === 2
            && segments[0].toLowerCase() === 'catalogues'
            && segments[1].toLowerCase() !== 'menu';
    }

    private userHasViewPermission(
        role: Role,
        moduleId: string,
        permIdToName: Map<string, string>,
    ): boolean {
        const grantedIds = role.accessPermissions?.[moduleId] ?? [];
        return grantedIds.some(id => permIdToName.get(id) === PERMISSION_VER);
    }

    /**
     * Renombres de tarjetas del menú por ruta. El título/descr. de cada
     * tarjeta viene del módulo en BD; este mapa permite renombrar en
     * frontend sin tocar la BD (ej. Activo Fijo → Equipos).
     *
     * `specialties` estaba aquí renombrado a "Coordinaciones". Se quitó: el
     * catálogo se llama Especialidades, y el `label` que ya trae el módulo
     * en BD dice justo eso, así que sin override sale bien solo.
     */
    private readonly cardOverrides: Record<string, { title: string; description?: string }> = {
        equipments: { title: '', description: 'Equipos por ubicación (alias) y su responsable.' },
    };

    private moduleToCard(m: ModuleModel): CatalogueCard {
        const override = this.cardOverrides[this.routeKey(m.route ?? '')];
        // La etiqueta del módulo (editable en Módulos) es el nombre del catálogo
        // en la tarjeta, la pantalla, el menú lateral y el Excel. El mapa de
        // catalogue-titles.ts solo cubre módulos sin etiqueta.
        const title = (m.label || m.name || '').trim() || override?.title || catalogueTitle(m.route, '(Sin nombre)');
        return {
            title,
            description: override?.description || m.description?.trim() || `Administración de ${title.toLowerCase()}.`,
            icon:        m.icon  || 'layers',
            route:       m.route || '/',
        };
    }

    /**
     * Navegación. La `route` ya viene absoluta desde el módulo de BD
     * (ej. '/catalogues/warehouses').
     */
    navigateTo(route: string): void {
        this.router.navigate([route]);
    }

    // ─── Conteo de registros por catálogo ──────────────────────────────
    //
    // El usuario pidió ver, en la parte superior del menú, cuántos
    // registros existen dados de alta en cada ruta. Para eso, por cada
    // tarjeta hacemos un search({ searchType: 'default', condition:
    // { deleted: false } }) y guardamos `data.length` en `routeCounts`.
    // Si la ruta no tiene servicio mapeado, queda undefined y el badge
    // no se muestra para esa tarjeta.

    /** Helper de plantilla: lee el conteo actual de una ruta. */
    countFor(route: string): number | undefined {
        return this.routeCounts()[route];
    }

    /** Extrae el último segmento de la ruta para mapearlo al servicio. */
    private routeKey(route: string): string {
        const parts = (route ?? '').split('?')[0].split('#')[0]
            .replace(/^\/+|\/+$/g, '').split('/').filter(s => s.length > 0);
        return parts[parts.length - 1] ?? '';
    }

    /**
     * Mapa de "último segmento de la ruta" → servicio capaz de contar.
     * Las llaves coinciden con los `setRoutes()` del catalogue-template
     * para no divergir en nomenclatura.
     */
    private serviceForRoute(route: string): CountableService | undefined {
        const key = this.routeKey(route);
        const map: Record<string, CountableService> = {
            affiliates:             this.affiliateService,
            affiliate:              this.affiliateService,
            brands:                 this.brandService,
            clients:                this.clientService,
            equipments:             this.equipmentsService,
            hospitalServices:       this.hospitalServicesService,
            kits:                   this.kitService,
            // Faltaban: sin servicio en este mapa la tarjeta no mostraba contador.
            doctors:                this.doctorsService,
            substitutes:            this.substitutesService,
            instrumentals:          this.instrumentalService,
            manufacturers:          this.manufacturerService,
            measurementInstruments: this.measurementInstrumentsService,
            medicalGasTanks:        this.medicalGasTanksService,
            measurementUnits:       this.measurementUnitsService,
            paymentMethods:         this.paymentMethodsService,
            places:                 this.placesService,
            procedures:             this.procedureService,
            products:               this.productsService,
            spareParts:             this.sparePartsService,
            specialties:            this.specialtyService,
            suppliers:              this.supplierService,
            warehouses:             this.warehousesService,
            courses:                this.coursesService,
            taxRegimes:             this.taxRegimesService,
            accessories:            this.accessoriesService,
            peripheralEquipment:    this.peripheralEquipmentService,
            fixedAssetConfigurations:        this.fixedAssetConfigurationsService,
            fixedAssetServiceConfigurations: this.fixedAssetServiceConfigurationsService,
            supplies:               this.suppliesService,
            institutions:           this.institutionsService,
        };
        return map[key];
    }

    /**
     * Dispara, en paralelo, un search por cada tarjeta visible para
     * calcular cuántos registros tiene cada catálogo.
     *
     * Importante: no esperamos secuencialmente. Cada respuesta actualiza
     * `routeCounts` y el badge correspondiente aparece en cuanto llega
     * (UX progresiva). Errores individuales se silencian para que un
     * catálogo caído no rompa el badge de los demás.
     */
    private loadRouteCounts(): void {
        for (const card of this.menuCards()) {
            const service = this.serviceForRoute(card.route);
            if (!service) continue;

            service
                .search({ searchType: 'default', condition: { deleted: false } })
                .then((resp: any) => {
                    if (resp?.state === 'success' && Array.isArray(resp.data)) {
                        this.routeCounts.update(curr => ({
                            ...curr,
                            [card.route]: resp.data.length,
                        }));
                    }
                })
                .catch(err => {
                    console.warn(`[CataloguesMenu] count failed for ${card.route}`, err);
                });
        }
    }
}
